import { useCallback, useEffect, useRef } from 'react';
import * as Y from 'yjs';

import { ActionCableProvider } from '../providers/action-cable-provider';

export const useCollaborativeDocumentSync = (
	yjsDoc: Y.Doc | null,
	provider: ActionCableProvider | null,
	getHTMLContent: () => string,
	throttleMs: number = 2000
) => {
	const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastSyncRef = useRef<number>(0);

	const sendPersistSnapshot = useCallback(() => {
		if (!provider || !yjsDoc) return;

		const now = Date.now();
		const timeSinceLastSync = now - lastSyncRef.current;

		if (timeSinceLastSync < throttleMs) {
			return;
		}

		try {
			const htmlContent = getHTMLContent();
			provider.sendPersistSnapshot(htmlContent);
			lastSyncRef.current = now;
		} catch (error) {
			console.error('[useCollaborativeDocumentSync] Failed to send persist snapshot:', error);
		}
	}, [provider, yjsDoc, getHTMLContent, throttleMs]);

	const schedulePersistSnapshot = useCallback(() => {
		if (throttleTimeoutRef.current) {
			clearTimeout(throttleTimeoutRef.current);
		}

		throttleTimeoutRef.current = setTimeout(() => {
			sendPersistSnapshot();
			throttleTimeoutRef.current = null;
		}, throttleMs);
	}, [sendPersistSnapshot, throttleMs]);

	useEffect(() => {
		return () => {
			if (throttleTimeoutRef.current) {
				clearTimeout(throttleTimeoutRef.current);
			}
		};
	}, []);

	return {
		sendPersistSnapshot,
		schedulePersistSnapshot,
	};
};
